import { IAppContext } from 'App/AppContext';
import dayjs from 'dayjs';
import { TFunction } from 'i18next';
import { WeekIndex } from 'microsoft-graph';
import { AuditYearStart } from 'models/setting';
import { i18nBase } from '../services/Localization/i18n';

//
// Date manipulation using Dayjs
//

// Add/substract date and time
export function addDateTimeHours(date1: Date, hours: number): Date {
  if (hours < 0) {
    return dayjs(date1).subtract(Math.abs(hours), 'hour').toDate();
  } else {
    return dayjs(date1).add(hours, 'hour').toDate();
  }
}

export function addDateTimeDays(date1: Date, days: number): Date {
  if (days < 0) {
    return dayjs(date1).subtract(Math.abs(days), 'day').toDate();
  } else {
    return dayjs(date1).add(days, 'day').toDate();
  }
}

export function addDateTimeYears(date1: Date, years: number): Date {
  if (years < 0) {
    return dayjs(date1).subtract(Math.abs(years), 'year').toDate();
  } else {
    return dayjs(date1).add(years, 'year').toDate();
  }
}

export function addDateTimeMinutes(date1: Date, mins: number): Date {
  if (mins < 0) {
    return dayjs(date1).subtract(Math.abs(mins), 'minute').toDate();
  } else {
    return dayjs(date1).add(mins, 'minute').toDate();
  }
}

// Diff functions
export function getDateTimeDiff(date1: Date | undefined, date2: Date | undefined): boolean {
  if (date1 === undefined && date2 === undefined) return false;
  if (date1 === undefined || date2 === undefined) return true;

  return dayjs(date1).diff(date2) !== 0;
}

export function getDateTimeDiffMinute(date1: Date, date2: Date): number {
  return dayjs(date1).diff(date2, 'minute');
}

export function getDateTimeDiffHours(date1: Date, date2: Date): number {
  return dayjs(date1).diff(date2, 'hour');
}

export function getDateTimeDiffDays(date1: Date, date2: Date): number {
  return dayjs(date1).diff(date2, 'day');
}

export function getDateTimeDiffMonths(date1: Date, date2: Date): number {
  return dayjs(date1).diff(date2, 'month');
}

export function getDateDiffDays(date1: Date, date2: Date): number {
  const date1NoTime = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate(), 0, 0, 0);
  const date2NoTime = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate(), 0, 0, 0);

  return dayjs(date1NoTime).diff(date2NoTime, 'day');
}

// add substract date only
export function addDateDays(date1: Date, days: number): Date {
  if (days < 0) {
    return dayjs(date1).subtract(Math.abs(days), 'day').toDate();
  } else {
    return dayjs(date1).add(days, 'day').toDate();
  }
}

export function addDateMonths(date1: Date, months: number): Date {
  if (months < 0) {
    return dayjs(date1).subtract(Math.abs(months), 'month').toDate();
  } else {
    return dayjs(date1).add(months, 'month').toDate();
  }
}

export function addDateYears(date1: Date, years: number): Date {
  if (years < 0) {
    return dayjs(date1).subtract(Math.abs(years), 'year').toDate();
  } else {
    return dayjs(date1).add(years, 'year').toDate();
  }
}

// This formats everything based on the current language
export function getLocaleTimeString(date?: Date, options?: Intl.DateTimeFormatOptions) {
  return date ? new Date(date).toLocaleTimeString(i18nBase.language, options) : '';
}

export function getLocaleDateString(date?: Date, options?: Intl.DateTimeFormatOptions) {
  return date ? new Date(date).toLocaleDateString(i18nBase.language, options) : '';
}

export function getLocaleDateTimeString(
  date?: Date,
  dateOptions?: Intl.DateTimeFormatOptions,
  timeOptions?: Intl.DateTimeFormatOptions,
) {
  if (!date) {
    return '';
  }
  const newDate = new Date(date);

  return (
    newDate.toLocaleDateString(i18nBase.language, dateOptions) +
    ' ' +
    newDate.toLocaleTimeString(i18nBase.language, timeOptions)
  );
}

//
// Date formatting functions
//
export function toLocaleDateNumeric(date?: Date): string {
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };

  return getLocaleDateString(date, options);
}

export function toLocaleDateShort(date?: Date): string {
  const options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' };

  return getLocaleDateString(date, options);
}

export function toLocaleDateLong(date?: Date): string {
  const options: Intl.DateTimeFormatOptions = { dateStyle: 'full' };

  return getLocaleDateString(date, options);
}

// "March 2027" - the period an audit occurrence is named after (mirrors the server naming)
export function toLocaleMonthYear(date?: Date): string {
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long' };

  return getLocaleDateString(date, options);
}

export function toLocaleDateShortNoYear(date?: Date): string {
  const options: Intl.DateTimeFormatOptions = { year: undefined, month: 'short', day: 'numeric' };

  return getLocaleDateString(date, options);
}

// "August 3" - a day inside a period the reader already knows the year of (an audit day)
export function toLocaleDateLongNoYear(date?: Date): string {
  const options: Intl.DateTimeFormatOptions = { year: undefined, month: 'long', day: 'numeric' };

  return getLocaleDateString(date, options);
}

//
// Date and time formatting functions
//
export function toLocaleDateTime(date?: Date): string {
  const dateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  };

  return getLocaleDateTimeString(date, dateOptions, timeOptions);
}

export function toLocaleDateTimeNoSeconds(date?: Date): string {
  const dateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    second: undefined,
    hour12: false,
  };

  return getLocaleDateTimeString(date, dateOptions, timeOptions);
}

export function toLocaleDateTimeShort(date?: Date): string {
  const dateOptions: Intl.DateTimeFormatOptions = { year: undefined, month: 'short', day: 'numeric' };
  const timeOptions: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };

  return getLocaleDateTimeString(date, dateOptions, timeOptions);
}

export function toLocaleDateTimeMedium(date?: Date): string {
  const dateOptions: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
  const timeOptions: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };

  return getLocaleDateTimeString(date, dateOptions, timeOptions);
}

export function toLocaleDateTimeLong(date?: Date): string {
  const dateOptions: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
  const timeOptions: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };

  return getLocaleDateTimeString(date, dateOptions, timeOptions);
}

export function toLocaleDateTimeMediumWithDay(date?: Date): string {
  const dateOptions: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric', weekday: 'short' };
  const timeOptions: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };

  return getLocaleDateTimeString(date, dateOptions, timeOptions);
}

//
// Time formatting functions
//
export function toLocaleTime(date?: Date): string {
  const options: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', second: '2-digit' };

  return getLocaleTimeString(date, options);
}

export function toLocaleTimeNoSeconds(date?: Date): string {
  const options: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', second: undefined, hour12: false };

  return getLocaleTimeString(date, options);
}

//
// API date time conversion
//
export function toApiDateTimeOptional(date: Date | undefined): string | undefined {
  // convert the date object into a ISO 8601 date string in the UTC time zone
  if (!date) {
    return undefined;
  }

  return toApiDateTimeRequired(date);
}

export function toApiDateTimeRequired(date: Date): string {
  // convert the date object into a ISO 8601 date string in the UTC time zone
  const dateString = date.toISOString();

  return dateString;
}

export function fromApiDateTimeOptional(dateString: string | undefined): Date | undefined {
  // convert the ISO 8601 date string in the UTC time zone to a date object in the local time zone
  if (!dateString) {
    return undefined;
  }

  return fromApiDateTimeRequired(dateString);
}

export function isDST(date: Date): boolean {
  const jan = new Date(date.getFullYear(), 0, 1);
  const jul = new Date(date.getFullYear(), 6, 1);

  return date.getTimezoneOffset() < Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
}

//The IANA zone the browser reports, such as Europe/Amsterdam. This is the only thing the client should
//send as a time zone: a UTC offset does not identify a zone, because zones sharing an offset on one date
//differ on another, and the API cannot tell which one the browser meant.
export function getBrowserTimeZoneCode(): string | undefined {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
}

export function fromApiDateTimeRequired(dateString: string): Date {
  // convert the ISO 8601 date string in the UTC time zone to a date object in the local time zone
  const date = dayjs(dateString).toDate();

  return date;
}

export function fromApiDateTimeToDateOptional(dateString: string | undefined): Date | undefined {
  // Converts an ISO 8601 instant to the local calendar day it falls on, without time.
  // Date-only values use `fromApiDateOptional`.
  if (!dateString) {
    return undefined;
  }

  return toDateOnly(fromApiDateTimeRequired(dateString));
}

export function fromApiDateOptional(dateString: string | undefined): Date | undefined {
  // convert the ISO 8601 date string in the UTC time zone to a date object without time (set to zero)
  if (!dateString) {
    return undefined;
  }

  return fromApiDateRequired(dateString);
}

export function fromApiDateRequired(dateString: string): Date {
  // convert an ISO 8601 date-only string (a calendar date stored as UTC midnight, see toApiDateRequired)
  // to a local date object without time. Take the UTC components so the calendar day survives in every
  // time zone: parsing "2026-08-03T00:00:00Z" behind UTC would otherwise land on August 2.
  const parsed = new Date(dateString);

  return new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate(), 0, 0, 0, 0);
}

// A calendar date the server stores as UTC midnight, read back as that SAME calendar day in local
// time. Kept as an alias of fromApiDateOptional, which now takes the UTC components as well.
export function fromApiDateOnly(dateString: string | undefined): Date | undefined {
  return fromApiDateOptional(dateString);
}

export function toApiDateOptional(date: Date | undefined): string | undefined {
  // convert the date object into a ISO 8601 date string without time (set to zero)
  if (!date) {
    return undefined;
  }

  return toApiDateRequired(date);
}

export function toApiDateRequired(date: Date): string {
  // convert the date object into a ISO 8601 date string without time (set to zero)
  const timeStr = 'T00:00:00.000Z';
  const dateStr =
    date.getFullYear() + '-' + ('0' + (date.getMonth() + 1)).slice(-2) + '-' + ('0' + date.getDate()).slice(-2);
  const result = dateStr + timeStr;

  return result;
}

//
// Constant values
//
export const weekDays0: days[] = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const weekDays1: days[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

//
// Other utils
//
export const isLastOfMonth = (date: Date): boolean => {
  const oneWeekAfter = addDateDays(date, 7);

  return oneWeekAfter.getMonth() !== date.getMonth();
};

export const getWeekIndex = (date: Date): WeekIndex => {
  const day = date.getDate();
  if (day <= 7) return 'first';
  if (day <= 14) return 'second';
  if (day <= 21) return 'third';
  if (day <= 28) return 'fourth';

  return 'last';
};

export const toDateOnly = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
};

export const roundTimeToNextHalfHour = (date: Date): Date => {
  let output = new Date(date);
  if (output.getMinutes() < 30) {
    output = addDateTimeMinutes(output, 30 - output.getMinutes());
  } else if (output.getMinutes() > 30) {
    output = addDateTimeMinutes(output, 60 - output.getMinutes());
  }

  return output;
};

export function isValidDate(date?: Date | string): boolean {
  try {
    if (!date) return false;
    let dateInternal: Date;
    if (typeof date === 'string') {
      dateInternal = new Date(date);
    } else {
      dateInternal = date;
    }
    if (Object.prototype.toString.call(dateInternal) === '[object Date]') {
      if (isNaN(dateInternal.getTime())) {
        return false;
      } else {
        return true;
      }
    } else {
      return false;
    }
  } catch {
    return false;
  }
}

export function setNewDateKeepTime(newDate: Date | undefined, currentDateTime: Date | undefined): Date {
  if (!newDate) {
    return currentDateTime ?? new Date();
  }
  if (!currentDateTime) {
    return newDate;
  }

  const newDateTime: Date = new Date(newDate);
  //create the new start date object, keep the time that was set
  newDateTime.setHours(currentDateTime.getHours());
  newDateTime.setMinutes(currentDateTime.getMinutes());
  newDateTime.setSeconds(0);
  newDateTime.setMilliseconds(0);

  return newDateTime;
}

export function setNewTimeKeepDate(currentDateTime: Date | undefined, hours: number, minutes: number): Date {
  if (!currentDateTime) {
    currentDateTime = new Date();
  }
  const newDateTime: Date = new Date(currentDateTime);
  //create the new start date object, set the date but keep the time that was set
  newDateTime.setHours(hours);
  newDateTime.setMinutes(minutes);
  newDateTime.setSeconds(0);
  newDateTime.setMilliseconds(0);

  return newDateTime;
}

export const isAm = (): boolean => {
  return dayjs().format('A') === 'AM';
};

export const getDays = (month: number): number[] => {
  const days = Array.from({ length: 31 }, (v, k) => k + 1);
  if ([1, 3, 5, 7, 8, 10, 12].includes(month)) {
    return days;
  } else if ([4, 6, 9, 11].includes(month)) {
    return days.slice(0, days.length - 1);
  } else if (month === 2) {
    return days.slice(0, days.length - 3);
  } else {
    return [];
  }
};

export const getDayNumeric = (day: days): number => {
  if (day === 'Sunday') return 0;
  if (day === 'Monday') return 1;
  if (day === 'Tuesday') return 2;
  if (day === 'Wednesday') return 3;
  if (day === 'Thursday') return 4;
  if (day === 'Friday') return 5;
  if (day === 'Saturday') return 6;

  return 0;
};

export type days = 'Sunday' | 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday';

export const parseTime = (input: string): string | undefined => {
  if (!input) return undefined;

  // Fast path: if input contains “:”
  if (input.includes(':')) {
    const parts = input.split(':');
    if (parts.length !== 2) return undefined;

    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);

    if (
      Number.isInteger(hours) &&
      Number.isInteger(minutes) &&
      hours >= 0 &&
      hours <= 23 &&
      minutes >= 0 &&
      minutes <= 59
    ) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }

    return undefined;
  }

  // Otherwise: digits only interpretation rules
  const digits = input.replace(/\D/g, '');
  if (!digits) return undefined;

  let hours: number;
  let minutes: number;

  switch (digits.length) {
    case 1: // "9" -> 09:00
    case 2: // "09" -> 09:00
      hours = Number(digits);
      minutes = 0;
      break;
    case 3: // "915" -> 09:15, "121" -> 01:21
      hours = Number(digits[0]);
      minutes = Number(digits.slice(1));
      break;
    case 4: // "1210" -> 12:10
      hours = Number(digits.slice(0, 2));
      minutes = Number(digits.slice(2));
      break;
    default:
      return undefined;
  }

  if (hours > 23 || minutes > 59) {
    return undefined;
  }

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

export const isSameDay = (date1: Date | undefined, date2: Date | undefined): boolean => {
  if (date1 === undefined && date2 === undefined) return true;
  if (date1 === undefined || date2 === undefined) return false;

  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

//
// Audit year functions
//
export const getCurrentAuditYear = (auditStart: string | undefined): number => {
  const currentDate = new Date();
  const auditYearStartDate = getAuditStartForSetting(currentDate.getFullYear(), auditStart);

  if (auditYearStartDate.getMonth() > currentDate.getMonth()) {
    return currentDate.getFullYear() - 1;
  } else if (auditYearStartDate.getMonth() === currentDate.getMonth()) {
    if (auditYearStartDate.getDate() > currentDate.getDate()) {
      return currentDate.getFullYear() - 1;
    }
  }

  return currentDate.getFullYear();
};

export const getAuditStartForSetting = (auditYear: number | undefined, auditStart: string | undefined): Date => {
  const output = new Date();
  if (!auditStart) auditStart = '01-01'; // Default to January 1st if no audit start date is set
  const year = auditYear || getCurrentAuditYear(auditStart);

  const split = auditStart.split('-');
  const month = +split[0];
  const date = +split[1];

  output.setFullYear(year);
  output.setDate(date);
  output.setMonth(month - 1);

  output.setHours(12);
  output.setMinutes(0);
  output.setSeconds(0);
  output.setMilliseconds(0);

  return output;
};

export const getAuditStart = (appContext: IAppContext, auditYear?: number): Date => {
  try {
    const auditDateSetting = appContext.globalDataCache.settings.get(AuditYearStart);

    return getAuditStartForSetting(auditYear, auditDateSetting);
  } catch {
    return new Date();
  }
};

// A turn shorter than this shows no "Thought for" line (PLAN-070)
export const thoughtForMinMs = 3000;

// The running "Thinking" line shows the elapsed time from this point on (PLAN-070)
export const thinkingElapsedMinMs = 10000;

// An elapsed time: under a minute whole seconds with the unit ("15 s"), from a minute "m:ss" ("1:24"), from an hour
// "h:mm:ss". The unit comes from copilot:Chat.Seconds.
export function formatElapsed(ms: number, t: TFunction<string[]>): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  if (totalSeconds < 60) {
    return t('copilot:Chat.Seconds', { count: totalSeconds });
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = String(totalSeconds % 60).padStart(2, '0');

  return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}` : `${minutes}:${seconds}`;
}
